import { Dispatch, SetStateAction, useMemo } from 'react';
import {
  useNavigate,
  useMatch,
  useLocation,
  type Location,
} from 'react-router';
import { stringEqualIgnoreCase } from '../../../Utils/stringUtils';
import base64url from 'base64url';
import { enumFromStringValue } from '../../../Utils/utils';
import React from 'react';

export interface ILoginInfo {
  system: System | undefined;
  database: string | undefined;
  rest: string | undefined;
  username: string | undefined;
}

export interface IBaseRouteParams {
  routingVersionPath?: string;
  systemPath?: string;
  databasePath?: string;
  usernamePath?: string;
}
export interface IBaseRouteValues {
  routingVersion?: string;
  system?: System;
  database?: string;
  username?: string;
}
export enum System {
  Lokalt = 'Lokalt',
  GAS = 'GAS',
}

export const currentRoutingVersion = '1';

//Alla routes börjar med en versionssiffra från version 1. Version 0 saknar den och börjar då på något av värdena nedanför.
export const v0routeNames: String[] = [System.Lokalt, System.GAS, 'GBS'];

export const encodeReadAbleBaseParamValue = (value?: string): string =>
  value == undefined || value === '' ? '_' : value;

export const decodeReadableBaseParamValue = (
  value?: string
): string | undefined =>
  value == undefined || value == '_' ? undefined : value;

export type BaseParamEncoding = 'none' | 'base64url';

export const encodeBaseParamValue = (
  value: string | undefined,
  encoding: BaseParamEncoding
): string =>
  value == undefined || value === ''
    ? '_'
    : encoding === 'base64url'
    ? base64url.encode(value)
    : value;

export const decodeBaseParamValue = (
  value: string | undefined,
  encoding: BaseParamEncoding
): string | undefined =>
  value == undefined || value == '_'
    ? undefined
    : encoding === 'base64url'
    ? base64url.decode(value)
    : value;

export const baseRouteValuesAreComplete = (values: IBaseRouteValues): boolean =>
  !!(
    values.routingVersion &&
    values.system &&
    values.database &&
    values.username
  );

export const baseRoutePath =
  '/:routingVersionPath/:systemPath/:usernamePath/:databasePath';

export const buildBasePath = (values: IBaseRouteValues): string =>
  `/${encodeReadAbleBaseParamValue(
    values.routingVersion
  )}/${encodeReadAbleBaseParamValue(values.system)}/${encodeBaseParamValue(
    values.username,
    'base64url'
  )}/${encodeBaseParamValue(values.database, 'none')}`;

type BaseRouteParams = [
  IBaseRouteValues & IBaseRouteParams,
  Dispatch<SetStateAction<IBaseRouteValues>>
];
export const BaseRouteContext: React.Context<BaseRouteParams> =
  React.createContext([{}, _ => {}]);

export const useBaseRouteContext = (): BaseRouteParams =>
  React.useContext(BaseRouteContext);

/** Context som bara innehåller value från BaseRouteContext, för bättre prestanda */
export const BaseRouteValueContext: React.Context<
  IBaseRouteValues & IBaseRouteParams
> = React.createContext({});

export const useBaseRouteValue = (): IBaseRouteValues & IBaseRouteParams =>
  React.useContext(BaseRouteValueContext);

export const useBaseRouteParams = (): BaseRouteParams => {
  const match = useMatch({ path: baseRoutePath, end: false });
  const params = match?.params as IBaseRouteParams | undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const databasePath = params?.databasePath;
  const usernamePath = params?.usernamePath;
  const systemPath = params?.systemPath;
  const routingVersionPath = params?.routingVersionPath;
  const value = useMemo(() => {
    const value: IBaseRouteValues & IBaseRouteParams = {
      routingVersionPath,
      routingVersion: routingVersionPath,
      systemPath,
      system: enumFromStringValue(System, systemPath),
      databasePath,
      database: decodeBaseParamValue(databasePath, 'none'),
      usernamePath,
      username: decodeBaseParamValue(usernamePath, 'base64url'),
    };
    return value;
  }, [databasePath, routingVersionPath, systemPath, usernamePath]);

  const result: [
    IBaseRouteValues & IBaseRouteParams,
    Dispatch<SetStateAction<IBaseRouteValues>>
  ] = useMemo(() => {
    const setter: Dispatch<SetStateAction<IBaseRouteValues>> = (
      action: SetStateAction<IBaseRouteValues>
    ) => {
      const newValues = typeof action === 'function' ? action(value) : action;
      const [_, _version, _system, _username, _database, ...rest] =
        location.pathname.split('/');
      const newLocation = `/${[
        currentRoutingVersion,
        newValues.system || System.Lokalt,
        encodeBaseParamValue(newValues.username, 'base64url'),
        encodeBaseParamValue(newValues.database, 'none'),
      ]
        .concat(rest)
        .join('/')}`;
      if (newLocation !== location.pathname) {
        navigate(newLocation, { replace: true });
      }
    };
    return [value, setter];
  }, [location, navigate, value]);
  return result;
};

export const getPathnameDb = (pathname: string) => {
  const pathParts = pathname.split('/');
  return pathParts[1] === 'SingleSignOn' ? pathParts[2] : pathParts[1];
};

export const pathWithUpdatedDbName = (pathname: string, dbName: string) => {
  const pathParts = pathname.split('/');
  if (pathParts[1] === 'SingleSignOn') {
    pathParts[3] = dbName;
  } else {
    pathParts[2] = dbName;
  }
  return pathParts.join('/');
};

const getURLLoginInfo = (location: Location): ILoginInfo => {
  const url = location?.pathname ?? '';
  const query = new URLSearchParams(location.search);
  const urlParts = url.split('/');
  return {
    system:
      urlParts.length > 1 && stringEqualIgnoreCase(urlParts[1], System.GAS)
        ? System.GAS
        : System.Lokalt,
    database: urlParts.length > 2 ? urlParts[2] : undefined,
    rest: urlParts.length > 3 ? urlParts.slice(3).join('/') : '',
    username: query.get('username') ?? undefined,
  };
};

export const useURLLoginInfo = () => {
  const location = useLocation();
  const urlLoginInfo = useMemo(() => getURLLoginInfo(location), [location]);
  return urlLoginInfo;
};
